import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL;

class User {
  getJobs() {
    return axios.get(API_URL + '/job');
  }

  async getQueries(offset = 0, limit = 50, search = '', start = '', end = '', queryType = '') {
    const params = new URLSearchParams()
    params.append('offset', offset)
    params.append('limit', limit)
    if (search) {
      params.append('search', search)
    }
    if (start) {
      params.append('start', start)
    }
    if (end) {
      params.append('end', end)
    }
    if (queryType) {
      params.append('query_type', queryType)
    }

    const { data } = await axios.get(`${API_URL}/account/queries?${params.toString()}`)
    return data
  }

  /**
   * Get user notifications with pagination and search
   * @param {number} offset - Pagination offset
   * @param {number} limit - Number of notifications to fetch
   * @param {string} search - Search query
   * @param {boolean} includeArchived - Include archived notifications
   */
  async getNotifications(offset = 0, limit = 50, search = '', includeArchived = false) {
    const params = new URLSearchParams()
    params.append('offset', offset)
    params.append('limit', limit)
    if (search) {
      params.append('search', search)
    }
    params.append('include_archived', includeArchived)

    const { data } = await axios.get(`${API_URL}/account/notifications?${params.toString()}`)
    return data
  }

  /**
   * Get single notification by ID
   * @param {string} notificationId - Notification ID
   */
  async getNotification(notificationId) {
    const { data } = await axios.get(`${API_URL}/account/notifications/${notificationId}`)
    return data
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   */
  async markNotificationRead(notificationId) {
    try {
      const { data } = await axios.post(`${API_URL}/account/notifications/${notificationId}/read`)
      return data
    } catch (errors) {
      return { status: "failed", error: errors.response }
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadNotificationCount() {
    try {
      const { data } = await axios.get(`${API_URL}/account/notifications/unread-count`)
      return data
    } catch (errors) {
      return { status: "failed", error: errors.response }
    }
  }

  /**
   * Archive notification
   * @param {string} notificationId - Notification ID
   */
  async archiveNotification(notificationId) {
    try {
      const { data } = await axios.post(`${API_URL}/account/notifications/${notificationId}/archive`)
      return data
    } catch (errors) {
      return { status: "failed", error: errors.response }
    }
  }

  /**
   * Get notification settings for saved searches
   */
  async getNotificationSettings() {
    try {
      const { data } = await axios.get(`${API_URL}/account/notification-settings`)
      return data
    } catch (errors) {
      return { status: "failed", error: errors.response }
    }
  }

  /**
   * Update notification settings for saved searches
   * @param {Object} settings - Notification settings
   * @param {boolean} settings.email_enabled - Enable email notifications
   * @param {string} settings.email_frequency - Email frequency (daily, weekly, monthly)
   * @param {number} settings.email_day - Day for weekly (0-6) or monthly (1-31)
   * @param {boolean} settings.inapp_enabled - Enable in-app notifications
   * @param {string} settings.inapp_frequency - In-app frequency (daily, weekly, monthly)
   * @param {number} settings.inapp_day - Day for weekly (0-6) or monthly (1-31)
   */
  async updateNotificationSettings(settings) {
    try {
      const { data } = await axios.put(`${API_URL}/account/notification-settings`, settings)
      return data
    } catch (errors) {
      return { status: "failed", error: errors.response }
    }
  }

  async getDetails() {
    const { data } = await axios.get(API_URL + '/account/details')
    return data; 
  }

  async save_query(query, type) {
    try {
      const { data } = await axios.post(API_URL + '/query/save', { query: query, type: type });
      return data
    } catch( errors ) {
      return { status: "failed", error: errors.response }
    }
  }

  async contact(email, name, topic, message) {
    try {
      const { data } = await axios.post(API_URL + '/contact', { email: email, name: name, topic: topic, message: message});
      return data
    } catch( errors ) {
      return { status: "failed", error: errors.response }
    }
  }

  async request_password_reset(email) {
    try {
      const { data } = await axios.get(API_URL + '/account/password-reset/'+email);
      return data
    } catch( errors ) {
      return { status: "failed", error: errors.response }
    }
  }

  async reset_password(token, new_password) {
    try {
      const { data } = await axios.post(API_URL + '/account/password-reset', { token: token, password: new_password});
      return data
    } catch( errors ) {
      return { status: "failed", error: errors.response }
    }
  }

  async getServices() {
    const { data } = await axios.get(API_URL + '/service')
    return data; 
  }

  async getDomains() {
    const { data } = await axios.get(API_URL + '/user/domains');
    return data
  }

  async getVerifiedDomains() {
    const { data } = await axios.get(API_URL + '/user/domains/verified');
    return data;
  }

  async getReports() {
    const { data } = await axios.get(API_URL + '/reports')
    return data; 
  }

  async getReport(id) {
    const { data } = await axios.get(API_URL + '/plan/report/result', { params: {report: id}})
    return data; 
  }

  async getStatus(id) {
    const { data } = await axios.get(API_URL + '/plan/reports/status', { params: {id: id}} )
    return data; 
  }

  async verifyDomain(domain) {
    const { data } = await axios.post(API_URL + '/user/domains/verify', { domain: domain} )
    return data; 
  }

  async removeDomain(domain) {
    const { data } = await axios.post(API_URL + '/user/domains/remove', { domain: domain} )
    return data; 
  }

  async list_api_keys() {
    const { data } = await axios.get(API_URL + '/account/list-keys')
    return data; 
  }

  async create_api_key(name, description) {
    try {
    const { data } = await axios.post(API_URL + '/account/create-key', { name: name, description: description} )
    return data; 
    } catch(exception) {
      return {status: "failed"}
    }
  }

  async delete_api_key(key_id) {
    const { data } = await axios.post(API_URL + '/account/delete-key', { id: key_id} )
    return data; 
  }

  listen_for_updates() {
    const ws = new WebSocket("ws://localhost:3010/api/plan/reports/status")
    
    return ws; 
  }

  async addDomain(domain) {
    const { data } = await axios.post(API_URL + '/user/domains/add', {
      domain: domain
    });
    return data;
  }

  createJob(serviceId) {
    return axios.post(API_URL + "/job/create", 
                    { 
                        service: serviceId,
                    }, 
                );
  }
}

export default new User();
